import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import '../styles/popover.css';

/**
 * Botao que abre um painel ancorado nele.
 *
 * ── Por que portal, de novo ──
 * Mesmo motivo do tooltip: a coluna de filtros vive dentro de um card com
 * `overflow: hidden` (`.card--flush`), e um painel renderizado ali dentro seria
 * cortado no primeiro pixel que passasse da borda. `position: fixed` no `body`
 * escapa disso sem que nenhum ancestral precise saber que ele existe.
 *
 * ── O que ele NAO faz ──
 * Nao decide selecao. Recebe `children` e deixa quem usa montar a lista — um
 * filtro de status escolhe UM, o de clientes escolhe VARIOS, e um componente
 * que tentasse servir aos dois viraria uma arvore de props booleanas.
 */
export function Popover({
  rotulo,
  resumo,
  ativo = false,
  children,
}: {
  /** Fica no `aria-label`: o que este filtro filtra. */
  readonly rotulo: string;
  /** O texto do botao — normalmente a selecao atual, resumida. */
  readonly resumo: string;
  /** Muda a aparencia quando ha filtro aplicado, para ele nao passar batido. */
  readonly ativo?: boolean;
  readonly children: (fechar: () => void) => ReactNode;
}): ReactElement {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();

  function abrir(): void {
    const r = botao.current?.getBoundingClientRect();
    if (r === undefined) return;
    setPos({ x: r.left, y: r.bottom + 6 });
    setAberto(true);
  }

  // Clique fora e `Esc` fecham. O clique e escutado na fase de CAPTURA para
  // fechar antes de o alvo processar o proprio clique — senao clicar em outro
  // filtro abriria o segundo e fecharia o primeiro na ordem errada, piscando.
  useEffect(() => {
    if (!aberto) return;

    function noDocumento(e: MouseEvent): void {
      const alvo = e.target as Node;
      if (painel.current?.contains(alvo) === true) return;
      if (botao.current?.contains(alvo) === true) return;
      setAberto(false);
    }
    function naTecla(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setAberto(false);
        botao.current?.focus();
      }
    }
    // Rolar leva o painel para longe do botao que o ancora. Mas so a rolagem de quem
    // CONTEM o botao: a conversa ao lado rola sozinha ate o fim quando carrega, e
    // fechar nela fazia o primeiro clique depois de abrir a tela nao abrir nada.
    function aoRolar(e: Event): void {
      const alvo = e.target;
      if (alvo instanceof Node && botao.current !== null && alvo.contains(botao.current)) {
        setAberto(false);
      }
    }

    document.addEventListener('mousedown', noDocumento, true);
    document.addEventListener('keydown', naTecla);
    window.addEventListener('scroll', aoRolar, true);
    return () => {
      document.removeEventListener('mousedown', noDocumento, true);
      document.removeEventListener('keydown', naTecla);
      window.removeEventListener('scroll', aoRolar, true);
    };
  }, [aberto]);

  return (
    <>
      <button
        ref={botao}
        type="button"
        className={`pop__btn${ativo ? ' is-on' : ''}${aberto ? ' is-open' : ''}`}
        onClick={() => (aberto ? setAberto(false) : abrir())}
        aria-haspopup="true"
        aria-expanded={aberto}
        aria-controls={id}
        aria-label={rotulo}
      >
        {/* O rotulo precisa ser um ELEMENTO para poder truncar: `text-overflow`
            nao age sobre no de texto solto, e sem isso um nome de clinica longo
            empurra a seta para fora do botao — que foi como o desalinhamento
            apareceu. */}
        <span className="pop__btn-label">{resumo}</span>
        <Icon name="chevron-right" size={13} />
      </button>

      {!aberto || pos === null
        ? null
        : createPortal(
            <div
              ref={painel}
              id={id}
              className="pop"
              role="dialog"
              aria-label={rotulo}
              style={{ left: pos.x, top: pos.y }}
            >
              {children(() => setAberto(false))}
            </div>,
            document.body,
          )}
    </>
  );
}
